import React, { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import FileUploader from "@/components/FileUploader";
import { toast } from "sonner";

/**
 * RequirementsForm — renders the buyer-facing requirement inputs from a
 * frozen `requirement_snapshot` saved on the order.
 *
 * Props:
 *   - snapshot: list of RequirementField (frozen at order-create time)
 *   - onSubmit({responses}): called with the structured responses payload
 */
export default function RequirementsForm({ snapshot, onSubmit }) {
  const fields = useMemo(
    () => (snapshot || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [snapshot],
  );
  const [values, setValues] = useState(() => {
    const v = {};
    fields.forEach((f) => {
      if (f.type === "multi_select") v[f.id] = { values: [] };
      else if (f.type === "file") v[f.id] = { files: [] };
      else v[f.id] = { value: "" };
    });
    return v;
  });
  const [submitting, setSubmitting] = useState(false);

  const setField = (id, patch) =>
    setValues((vs) => ({ ...vs, [id]: { ...vs[id], ...patch } }));

  const validate = () => {
    for (const f of fields) {
      if (!f.required) continue;
      const v = values[f.id] || {};
      if (f.type === "multi_select") {
        if (!v.values?.length) return `Required: ${f.prompt}`;
      } else if (f.type === "file") {
        if (!v.files?.length) return `Required: ${f.prompt}`;
      } else {
        if (!v.value || !v.value.trim()) return `Required: ${f.prompt}`;
      }
    }
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    const responses = fields.map((f) => {
      const v = values[f.id] || {};
      const base = { field_id: f.id, skipped: false };
      if (f.type === "multi_select") return { ...base, values: v.values || [] };
      if (f.type === "file") return { ...base, files: v.files || [] };
      const value = (v.value ?? "").trim();
      return { ...base, value };
    });
    setSubmitting(true);
    try {
      await onSubmit({ responses });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="structured-requirements-form">
      {fields.map((f, i) => (
        <div key={f.id} className="space-y-1.5" data-testid={`req-field-${f.type}-${i}`}>
          <Label className="text-sm font-bold text-mv-secondary">
            {f.prompt}
            {f.required && <span className="text-rose-600 ml-1">*</span>}
          </Label>
          {f.helper && <p className="text-xs text-gray-500 -mt-0.5">{f.helper}</p>}

          {f.type === "short_text" && (
            <Input
              value={values[f.id]?.value || ""}
              onChange={(e) => setField(f.id, { value: e.target.value })}
              data-testid={`req-input-${i}`}
            />
          )}

          {f.type === "long_text" && (
            <Textarea
              rows={4}
              value={values[f.id]?.value || ""}
              onChange={(e) => setField(f.id, { value: e.target.value })}
              data-testid={`req-input-${i}`}
            />
          )}

          {f.type === "url" && (
            <Input
              type="url" placeholder="https://"
              value={values[f.id]?.value || ""}
              onChange={(e) => setField(f.id, { value: e.target.value })}
              data-testid={`req-input-${i}`}
            />
          )}

          {f.type === "email" && (
            <Input
              type="email" placeholder="name@example.com"
              value={values[f.id]?.value || ""}
              onChange={(e) => setField(f.id, { value: e.target.value })}
              data-testid={`req-input-${i}`}
            />
          )}

          {f.type === "phone" && (
            <Input
              type="tel" placeholder="+1 555 0100"
              value={values[f.id]?.value || ""}
              onChange={(e) => setField(f.id, { value: e.target.value })}
              data-testid={`req-input-${i}`}
            />
          )}

          {f.type === "yes_no" && (
            <Select
              value={values[f.id]?.value || ""}
              onValueChange={(v) => setField(f.id, { value: v })}
            >
              <SelectTrigger data-testid={`req-input-${i}`}>
                <SelectValue placeholder="Select…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          )}

          {f.type === "single_select" && (
            <Select
              value={values[f.id]?.value || ""}
              onValueChange={(v) => setField(f.id, { value: v })}
            >
              <SelectTrigger data-testid={`req-input-${i}`}>
                <SelectValue placeholder="Select an option…" />
              </SelectTrigger>
              <SelectContent>
                {(f.options || []).map((opt) => (
                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {f.type === "multi_select" && (
            <div className="space-y-1.5" data-testid={`req-input-${i}`}>
              {(f.options || []).map((opt) => {
                const checked = (values[f.id]?.values || []).includes(opt);
                return (
                  <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) => {
                        const cur = values[f.id]?.values || [];
                        if (v) setField(f.id, { values: [...cur, opt] });
                        else setField(f.id, { values: cur.filter((x) => x !== opt) });
                      }}
                    />
                    {opt}
                  </label>
                );
              })}
            </div>
          )}

          {f.type === "file" && (
            <div data-testid={`req-input-${i}`}>
              <FileUploader
                folder="messages"
                value={(values[f.id]?.files || []).map((url) => ({
                  file_id: url.split("/").pop(),
                  filename: url.split("/").pop(),
                }))}
                onChange={(arr) => {
                  const urls = (arr || []).map(
                    (u) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${u.file_id}`,
                  );
                  setField(f.id, { files: urls });
                }}
                accept={
                  (f.file_types || []).length
                    ? f.file_types.map((t) => `.${t}`).join(",")
                    : undefined
                }
                max={f.max_files || 1}
                label={`Upload (max ${f.max_files || 1})`}
                testId={`req-file-${i}`}
              />
            </div>
          )}
        </div>
      ))}
      <Button
        onClick={submit}
        disabled={submitting}
        className="bg-mv-primary text-white font-bold"
        data-testid="submit-structured-req-btn"
      >
        {submitting ? "Submitting…" : "Submit requirements"}
      </Button>
    </div>
  );
}
